import { useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { WidgetViewMode, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import useCustomExcelFileName from 'hooks/useCustomExcelFileName';
import { useWidgetPillsAndFilters } from 'hooks/useWidgetPillsAndFilters';

import { useMutationExportCsvReport } from 'api/mutations/exportCsvReport.mutation';
import { useQueryCriticalPathAddedTableData } from 'api/queries/criticalPathAddedTableData.query';

import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { ITableLocalFiltersFields } from 'components/widgets/ChangeAnalysisTrackedActivities/tableLocalFilters/tableLocalFilters';
import { statusOptions } from 'components/widgets/KeyActivities/components/filter.utils';

export type UseCriticalPathAddedProvider = ReturnType<typeof useCriticalPathAddedProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    globalFilterData: any;
    localFilters: any;
    compareVersionId: number | undefined;
    latestVersionId: number | undefined;
    title: string;
    projectId: number | null | undefined;
    contractId: number | null | undefined;
    widgetViewMode: WidgetViewMode;
}

export const useCriticalPathAddedProvider = ({
    widgetId,
    globalFilterData,
    localFilters,
    compareVersionId,
    latestVersionId,
    title,
    projectId,
    contractId,
    widgetViewMode,
}: Props) => {
    const isInPinboard = Boolean(widgetId);
    const [activeTab, setActiveTab] = useState<TKeys>(
        WidgetViewModeUtils.isReportEditWidget(widgetViewMode) ||
            WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode)
            ? (localFilters?.activeTab as TKeys)
            : 'next',
    );

    const form = useForm<ITableLocalFiltersFields>({
        defaultValues: {
            status: statusOptions,
        },
    });

    const formValues = useWatch({ control: form.control });

    const source = isInPinboard
        ? { ...localFilters, activeTab }
        : {
              ...globalFilterData,
              statusFilter: formValues?.status,
              activeTab,
          };

    const { filters, pills } = useWidgetPillsAndFilters({ widgetId, source, latestVersionId });

    const getCriticalPathAddedTableDataPayload = {
        projectId: projectId,
        contractId: contractId,
        latestVersionId: latestVersionId,
        compareVersionId: compareVersionId,
        filter: {
            filters,
        },
    };

    const getCriticalPathAddedTableData = useQueryCriticalPathAddedTableData(getCriticalPathAddedTableDataPayload);

    const reportType = getCriticalPathAddedTableData?.data?.typeId || '';
    const fileName = useCustomExcelFileName({
        projectId,
        contractId,
        latestVersionId,
        title,
        prefix: 'Change_Analysis_',
    });

    const exportCsvReport = {
        instance: useMutationExportCsvReport(),
        payload: {
            compareVersionId,
            contractId,
            filter: { filters },
            latestVersionId,
            period: activeTab,
            projectId,
            reportType,
            fileName,
        },
    };

    return {
        ui: {
            state: {
                statusFilter: source.statusFilter,
                activityCodes: source.activityCodes,
                milestoneFragnet: source.milestoneFragnet,
                tags: source.tags,
                wbs: source.wbs,
                activeTab: source.activeTab,
            },
            setters: {
                setActiveTab,
            },
            isInPinboard,
            form,
            pills,
        },
        api: {
            getCriticalPathAddedTableData: {
                instance: getCriticalPathAddedTableData,
                payload: getCriticalPathAddedTableDataPayload,
            },
            exportCsvReport,
        },
    };
};
